// GoalsView.tsx — "what you're working toward" (Learn Spanish, Finish my novel…), not just a
// minutes target. Bogdan, 2026-07-31: "help people surface what they want to do... AI giving
// ideas, what's cool out there" — DISCOVERY, never prescriptive advice. Each goal shows its own
// time totals (mirrors the app's existing week-over-week shape) and an "Ideas" action that runs a
// real on-device web search (search.ts, Option 3 — no Oriora search key) and asks the brain to
// narrate ONLY those real results, cited, framed as "here's what's out there."
import { useCallback, useEffect, useState } from "react";
import { store, type Goal, type GoalStats } from "./store";
import { goalIdeas, BrainError } from "./brain";

function fmtMinutes(min: number): string {
  if (min < 60) return `${min}m`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return m ? `${h}h ${m}m` : `${h}h`;
}

function daysSince(iso: string | null): number | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return Math.max(0, Math.floor((Date.now() - d.getTime()) / 86_400_000));
}

function NewGoalForm({ onCreated }: { onCreated: () => void }) {
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);
  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const t = title.trim();
    if (!t || busy) return;
    setBusy(true);
    try {
      await store.createGoal({ title: t });
      setTitle("");
      onCreated();
    } finally {
      setBusy(false);
    }
  }
  return (
    <form onSubmit={submit} className="row" style={{ gap: "0.5rem", marginBottom: "1.25rem" }}>
      <input
        value={title} onChange={(e) => setTitle(e.target.value)} placeholder="What are you working toward? e.g. Learn Spanish"
        style={{ flex: 1 }} maxLength={80}
      />
      <button className="btn btn-primary" type="submit" disabled={busy || !title.trim()}>Add goal</button>
    </form>
  );
}

function GoalCard({ goal, onChanged }: { goal: Goal; onChanged: () => void }) {
  const [stats, setStats] = useState<GoalStats | null>(null);
  const [ideas, setIdeas] = useState<string | null>(null);
  const [loadingIdeas, setLoadingIdeas] = useState(false);
  const [ideasErr, setIdeasErr] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const load = useCallback(() => { void store.goalStats(goal.id).then(setStats); }, [goal.id]);
  useEffect(() => { load(); }, [load]);

  async function fetchIdeas() {
    setLoadingIdeas(true); setIdeasErr(null);
    try {
      const r = await goalIdeas(goal.title);
      setIdeas(r?.ideas || "Nothing concrete came back for this one — try again in a moment.");
    } catch (e) {
      setIdeasErr(e instanceof BrainError ? e.message : "Couldn't fetch ideas — please try again.");
    } finally {
      setLoadingIdeas(false);
    }
  }

  const since = stats ? daysSince(stats.last_session_at) : null;
  const trend =
    stats && stats.prior_week_minutes > 0
      ? stats.this_week_minutes >= stats.prior_week_minutes
        ? `up from ${fmtMinutes(stats.prior_week_minutes)} last week`
        : `down from ${fmtMinutes(stats.prior_week_minutes)} last week`
      : null;

  return (
    <div className="card card-in" style={{ padding: "1.1rem 1.25rem", marginBottom: "1rem", textAlign: "left" }}>
      <div className="row" style={{ justifyContent: "space-between", alignItems: "flex-start" }}>
        <div style={{ fontWeight: 700, fontSize: "var(--fs-md)" }}>{goal.title}</div>
        <button className="btn btn-ghost" style={{ fontSize: "var(--fs-xs)", padding: "0.2rem 0.4rem", color: "var(--ink-3)" }} onClick={() => setConfirmDelete(true)}>Remove</button>
      </div>
      {stats && (
        <div className="muted" style={{ fontSize: "var(--fs-sm)", color: "var(--ink-2)", marginTop: 4 }}>
          {stats.sessions_total === 0
            ? "No sessions tagged to this goal yet — pick it when you start a Focus Session."
            : `${fmtMinutes(stats.total_minutes)} total · ${stats.sessions_total} session${stats.sessions_total === 1 ? "" : "s"}` +
              (since !== null ? ` · ${since === 0 ? "worked on today" : `${since}d since you touched this`}` : "") +
              (trend ? ` · this week ${fmtMinutes(stats.this_week_minutes)} (${trend})` : "")}
        </div>
      )}
      <div style={{ marginTop: "0.75rem" }}>
        <button className="btn" style={{ fontSize: "var(--fs-sm)" }} disabled={loadingIdeas} onClick={() => void fetchIdeas()}>
          {loadingIdeas ? "Looking…" : "✦ Ideas — what's out there"}
        </button>
      </div>
      {ideasErr && <div style={{ marginTop: 8, color: "var(--danger)", fontSize: "var(--fs-xs)" }}>{ideasErr}</div>}
      {ideas && !ideasErr && (
        <p className="muted" style={{ marginTop: "0.75rem", fontSize: "var(--fs-sm)", color: "var(--ink-2)", lineHeight: 1.55, whiteSpace: "pre-line" }}>
          {ideas}
        </p>
      )}
      {confirmDelete && (
        <div className="modal-overlay">
          <div className="modal-card" style={{ width: 360 }}>
            <div style={{ fontWeight: 700, marginBottom: "0.6rem" }}>Remove "{goal.title}"?</div>
            <p className="muted" style={{ fontSize: "var(--fs-sm)", margin: "0 0 1rem", color: "var(--ink-2)" }}>
              Your logged focus time stays — sessions just lose the tag.
            </p>
            <div className="row" style={{ justifyContent: "flex-end", gap: "0.5rem" }}>
              <button className="btn btn-ghost" onClick={() => setConfirmDelete(false)}>Cancel</button>
              <button className="btn btn-danger" onClick={async () => { setConfirmDelete(false); await store.deleteGoal(goal.id); onChanged(); }}>Remove</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function GoalsView({ refreshSignal }: { refreshSignal?: number }) {
  const [goals, setGoals] = useState<Goal[]>([]);
  const reload = useCallback(() => { void store.listGoals().then(setGoals); }, []);
  // refreshSignal — a chat-driven goal change (create_goal) can happen while this tab is already
  // open (chat is an overlay, not a tab switch), so mount-only wouldn't pick it up. Mirrors
  // FocusPlanSection's refreshSignal prop.
  useEffect(() => { reload(); }, [reload, refreshSignal]);

  return (
    <div>
      <h1 style={{ fontSize: "var(--fs-heading)", fontWeight: 700, marginBottom: "1rem" }}>Goals</h1>
      <p className="muted" style={{ fontSize: "var(--fs-sm)", color: "var(--ink-2)", marginBottom: "1.25rem", lineHeight: 1.5 }}>
        What are you working toward? Tag a focus session to a goal to track time against it, and ask
        for ideas — real things people pursuing the same goal are actually into, not advice.
      </p>
      <NewGoalForm onCreated={reload} />
      {goals.length === 0 ? (
        <div className="card" style={{ padding: "2.5rem", textAlign: "center" }}>
          <div className="muted" style={{ color: "var(--ink-2)" }}>No goals yet — add what you're working toward above.</div>
        </div>
      ) : (
        goals.map((g) => <GoalCard key={g.id} goal={g} onChanged={reload} />)
      )}
    </div>
  );
}
